import axios from 'axios';
import rison from 'rison';
import { PropertySelections, PropertyOptions, PropertyValue, Configuration } from './types';

export async function resolveSelectionProposals(
    gavelUrl: string,
    tenantId: string,
    spaceId: string,
    proposals: PropertySelections[],
): Promise<{ selections: PropertySelections; options: { properties: PropertyOptions } }> {
    const proposalRison = encodeURIComponent(rison.encode_array(proposals));
    const selectionsRes = await axios.get(
        `${gavelUrl}/api/v1/${tenantId}/selections?space=${spaceId}&proposal=${proposalRison}`,
    );
    return selectionsRes.data;
}

export function isChoiceCompatible(
    compatiblePropertyOptions: PropertyOptions | undefined,
    property: string,
    value: PropertyValue,
): boolean {
    return (
        ((compatiblePropertyOptions && compatiblePropertyOptions[property]) ?? [null]).includes(
            value,
        ) ?? false
    );
}

export async function resolveChoice(
    gavelUrl: string,
    tenantId: string,
    spaceId: string,
    property: string,
    value: PropertyValue,
    compatiblePropertyOptions: PropertyOptions,
    currentSelections: PropertySelections,
    fixedSelections?: PropertySelections,
    fallbacks?: PropertySelections[],
): Promise<PropertySelections> {
    if (isChoiceCompatible(compatiblePropertyOptions, property, value)) {
        return { ...currentSelections, [property]: value };
    } else {
        const proposal = { [property]: value };
        const proposals = [
            fixedSelections ?? {},
            proposal,
            currentSelections ?? {},
            ...(fallbacks ?? []),
        ]
            .flatMap(Object.entries)
            .map(([key, val]) => ({
                [key]: val,
            }));

        return await resolveSelectionProposals(gavelUrl, tenantId, spaceId, proposals).then(
            ({ selections }) => selections,
        );
    }
}

export async function getConfiguration(
    gavelUrl: string,
    tenantId: string,
    spaceId: string,
    selections: PropertySelections,
): Promise<Configuration | null> {
    const encodedSelections = encodeURIComponent(rison.encode_object(selections));
    const configurationRes = await axios.get(
        `${gavelUrl}/api/v1/${tenantId}/configurations?space=${spaceId}&selections=${encodedSelections}`,
    );
    return configurationRes.data.configurations[0] ?? null;
}

export async function getPropertyOptions(
    gavelUrl: string,
    tenantId: string,
    spaceId: string,
    selections: PropertySelections,
): Promise<Record<string, Array<PropertyValue>>> {
    const encodedSelections = selections ? encodeURIComponent(rison.encode_object(selections)) : '';
    const validPropertiesRes = await axios.get(
        `${gavelUrl}/api/v1/${tenantId}/options?space=${spaceId}&selections=${encodedSelections}`,
    );
    return validPropertiesRes.data.properties;
}
